import { useEffect, useState } from "react";
import { getKeyLoanLogs } from "../fetch/KeyRequest";
import { toJSTDateTimeString } from "../function/toJSTDateString";
import styles from "../styles/modules/StorageLocationPopup.module.css";
import keyStyles from "../styles/modules/KeyPage.module.css";
import { KeyLoanDisp, KeyLoanLog } from "../types";

interface KeyLoanHistoryPopupProps {
  loan: KeyLoanDisp;
  // 表示対象の項目（押した「修正済み」の列）
  field: "borrowed_at" | "returned_at";
  onClose: () => void;
}

const FIELD_LABEL = {
  borrowed_at: "貸出時間",
  returned_at: "返却時間",
} as const;

// 「修正済み」を押したときに出す、その項目の修正履歴。
// 自動記録された元の値を先頭に置き、修正を古い順に並べる。
function KeyLoanHistoryPopup({ loan, field, onClose }: KeyLoanHistoryPopupProps) {
  const [logs, setLogs] = useState<KeyLoanLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      const all = await getKeyLoanLogs(loan.id);
      if (ignore) return;
      setLogs(all.filter((log) => log.action === "edit_time" && log.field === field));
      setIsLoading(false);
    })();
    return () => {
      ignore = true;
    };
  }, [loan.id, field]);

  const original =
    field === "borrowed_at" ? loan.borrowed_original : loan.returned_original;

  return (
    <div className={styles["overlay"]} onClick={onClose}>
      <div className={styles["panel"]} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles["title"]}>修正履歴</h3>
        <p className={keyStyles["history-sub"]}>
          {loan.storage_location_name ?? "-"} ／ {loan.user_name ?? "-"} ／{" "}
          {FIELD_LABEL[field]}
        </p>

        {isLoading ? (
          <p className={keyStyles["history-empty"]}>読み込み中…</p>
        ) : (
          <div className={keyStyles["history-list"]}>
            <div className={keyStyles["history-item"]}>
              <div className={keyStyles["history-meta"]}>自動記録</div>
              <div>
                {original ? toJSTDateTimeString(original) : "（記録なし）"}
              </div>
            </div>

            {logs.map((log) => (
              <div key={log.id} className={keyStyles["history-item"]}>
                <div className={keyStyles["history-meta"]}>
                  {log.created_at ? toJSTDateTimeString(log.created_at) : "-"} ・{" "}
                  {log.user_name ?? "不明なユーザー"} が修正
                </div>
                <div>
                  <span className={keyStyles["history-old"]}>
                    {log.before_value
                      ? toJSTDateTimeString(log.before_value)
                      : "未返却"}
                  </span>
                  →{" "}
                  <span className={keyStyles["history-new"]}>
                    {log.after_value
                      ? toJSTDateTimeString(log.after_value)
                      : "未返却"}
                  </span>
                </div>
              </div>
            ))}

            {logs.length === 0 && (
              <p className={keyStyles["history-empty"]}>修正履歴はありません。</p>
            )}
          </div>
        )}

        <button type="button" className={styles["close-btn"]} onClick={onClose}>
          閉じる
        </button>
      </div>
    </div>
  );
}

export default KeyLoanHistoryPopup;
